import React, {
  useEffect,
  useRef,
  useState,
  useContext,
} from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { Menu } from '@tmap/mmm-style-guide/src/Menu';
import { MenuItem } from '@tmap/mmm-style-guide/src/MenuItem';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@tmap/mmm-style-guide/src/Dialog';
import { Button } from '@tmap/mmm-style-guide/src/Button';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { IconButton } from '@tmap/mmm-style-guide/src/IconButton';
import { Close, Warning } from '@tmap/mmm-style-guide/src/Icon';
import useThunktank from '@tmap/mmm-core/redux/thunktank/useThunktank';
import stopEvent from '@tmap/mmm-style-guide/src/util/stopEvent';

import isUserModerator from '../../lib/community/isUserModerator';
import RefreshPinnedContext from '../../lib/community/refreshPinnedContext';
import { deleteContribution, togglePin } from '../../actions/contributions';

function DeleteContributionDialog(props) {
  const { contributionId, open, handleClose } = props;
  const [deleteState, sendDelete] = useThunktank('deleteContributionDialog')(deleteContribution);
  const handleSubmit = () => {
    sendDelete(contributionId);
  };
  useEffect(() => {
    if (deleteState.data) handleClose();
  }, [deleteState.data, handleClose]);

  return (
    <Dialog
      open={open}
      maxWidth='sm'
      keepMounted={false}
      onClose={handleClose}
    >
      <DialogTitle>
        <Grid container alignItems='center'>
          <Grid item sx={{ display: 'flex', paddingRight: '6px' }}>
            <Warning color='error' />
          </Grid>
          <Grid item xs>
            <Typography variant='body1' fontWeight='500'>Delete Contribution</Typography>
          </Grid>
          <Grid item>
            <IconButton size='small' onClick={handleClose}><Close /></IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <Typography variant='h6'>Are you sure?</Typography>
        <Typography variant='body2'>You are about to delete this contribution. The user will still be able to make further contributions. Are you sure you want to continue?</Typography>
      </DialogContent>
      <DialogActions sx={{ padding: 3, paddingTop: 1 }}>
        <Grid container spacing={1}>
          <Grid item sm={6} xs={12}>
            <Button fullWidth variant='outlined' color='inverted' onClick={handleClose}>Cancel</Button>
          </Grid>
          <Grid item sm={6} xs={12}>
            <Button fullWidth variant='contained' color='error' onClick={handleSubmit} loading={deleteState?.isLoading}>Delete Contribution</Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
}

function ContributionCardMenu(props) {
  const { contribution, visitCommunity, children } = props;
  const { slug, type = 'default' } = contribution;

  const router = useRouter();

  const refreshPinned = useContext(RefreshPinnedContext);

  // avoiding useCurrentUser here to avoid an unnecessary rerender for non-role profile info changes
  const userRoles = useSelector((state) => state?.users?.current?.roles, shallowEqual);
  const userId = useSelector((state) => state?.users?.current?.userId, shallowEqual);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef();
  const handleOpenMenu = () => { setMenuOpen(true); };
  const handleCloseMenu = () => { setMenuOpen(false); };

  const handleOpenEdit = () => { router.push(`/moving-living/${contribution.community.slug}/post?postId=${slug}&type=${type}`); };

  const [deleteOpen, setDeleteOpen] = useState(false);
  const handleOpenDelete = () => { setDeleteOpen(true); };
  const handleCloseDelete = () => { setDeleteOpen(false); };

  const [, sendTogglePinRequest] = useThunktank('pinContribution')(togglePin);
  const handlePin = () => {
    sendTogglePinRequest(contribution);
    if (refreshPinned) {
      refreshPinned(contribution);
    }
    handleCloseMenu();
  };

  const handleVisitCommunity = () => {
    router.push(`/moving-living/${contribution.community.slug}`);
  };

  const handleManage = () => {
    router.push(`/moving-living/${contribution.community.slug}/members#${contribution.authorId}`);
  };

  const isModerator = isUserModerator({ roles: userRoles }, contribution.communityId);
  const userIsAuthor = contribution.authorId === userId;

  const isPinnable = isModerator && !contribution.pinned && contribution.media?.length > 0;

  if (!isModerator && !userIsAuthor) return null;
  return (
    <Box onClick={stopEvent} ref={menuRef}>
      {children(handleOpenMenu)}
      <Menu
        open={menuOpen}
        onClose={handleCloseMenu}
        anchorEl={menuRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          style: {
            marginTop: '-8px',
          },
        }}
      >
        {visitCommunity && (
          <MenuItem onClick={handleVisitCommunity}>
            Visit Community Page
          </MenuItem>
        )}
        <MenuItem onClick={handleOpenEdit}>
          Edit Post
        </MenuItem>
        <MenuItem onClick={handleOpenDelete}>
          <Typography color='error'>Delete Post</Typography>
        </MenuItem>
        {isModerator && (
          <MenuItem onClick={handleManage}>
            Manage Author
          </MenuItem>
        )}
        {isModerator && isPinnable && (
          <MenuItem onClick={handlePin}>
            Pin Post
          </MenuItem>
        )}
        {isModerator && contribution.pinned && (
          <MenuItem onClick={handlePin}>
            Unpin Post
          </MenuItem>
        )}
      </Menu>
      {deleteOpen && (
        <DeleteContributionDialog
          open={deleteOpen}
          handleClose={handleCloseDelete}
          contributionId={contribution._id}
        />
      )}
    </Box>
  );
}

export default ContributionCardMenu;
